// Notification Pinia store

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { notificationApi, type NotificationResponse } from '@/api/notification'
import { getAccessToken, getRefreshToken, updateStoredTokens } from '@/api/axios'
import { v, assertValid } from '@/utils/validateStore'

const notificationSchema = v.shape({
  id: v.number,
  user_id: v.number,
  type: v.string,
  title: v.string,
  message: v.string,
  data: v.nullable(v.shape({})),
  is_read: v.boolean,
  read_at: v.nullable(v.string),
  created_at: v.string,
})

const unreadCountSchema = v.shape({
  count: v.number,
})

export const useNotificationStore = defineStore('notification', () => {
  // State
  const notifications = ref<NotificationResponse[]>([])
  const unreadCount = ref(0)
  const wsConnected = ref(false)
  const wsError = ref<string | null>(null)
  const isLoading = ref(false)
  const callbackAlert = ref<any>(null)

  // Private WebSocket instance
  let ws: WebSocket | null = null
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null
  let reconnectAttempts = 0
  const MAX_RECONNECT_ATTEMPTS = 5

  // Getters
  const unreadNotifications = computed(() =>
    notifications.value.filter((n) => !n.is_read)
  )

  const readNotifications = computed(() =>
    notifications.value.filter((n) => n.is_read)
  )

  // Actions
  const loadNotifications = async (unreadOnly = false, limit = 50) => {
    isLoading.value = true
    try {
      const data = await notificationApi.getNotifications(unreadOnly, limit)
      assertValid('notifications.loadNotifications', data, v.array(notificationSchema))
      notifications.value = data
      await fetchUnreadCount()
    } catch (error) {
      console.error('Failed to load notifications:', error)
    } finally {
      isLoading.value = false
    }
  }

  const fetchUnreadCount = async () => {
    try {
      const data = await notificationApi.getUnreadCount()
      assertValid('notifications.fetchUnreadCount', data, unreadCountSchema)
      unreadCount.value = data.count
    } catch (error) {
      console.error('Failed to fetch unread count:', error)
    }
  }

  const markAsRead = async (notificationId: number) => {
    try {
      await notificationApi.markAsRead(notificationId)
      const notification = notifications.value.find((n) => n.id === notificationId)
      if (notification) {
        notification.is_read = true
        notification.read_at = new Date().toISOString()
      }
      await fetchUnreadCount()
    } catch (error) {
      console.error('Failed to mark notification as read:', error)
    }
  }

  const markAllAsRead = async () => {
    try {
      await notificationApi.markAllAsRead()
      notifications.value.forEach((n) => {
        n.is_read = true
        n.read_at = new Date().toISOString()
      })
      unreadCount.value = 0
    } catch (error) {
      console.error('Failed to mark all as read:', error)
    }
  }

  const addNotification = (notification: NotificationResponse) => {
    assertValid('notifications.addNotification', notification, notificationSchema)
    notifications.value.unshift(notification)
    if (!notification.is_read) {
      unreadCount.value++
    }
  }

  // WebSocket connection — always builds the URL from the freshest access token.
  // We ignore the caller-supplied token because interceptors may refresh it while
  // this closure lives on.
  const initWebSocket = (_token?: string) => {
    const token = getAccessToken()
    if (!token) return

    if (ws?.readyState === WebSocket.OPEN) {
      return
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const wsUrl = `${protocol}//${window.location.host}/ws/notifications`

    try {
      ws = new WebSocket(`${wsUrl}?token=${token}`)

      ws.onopen = () => {
        wsConnected.value = true
        wsError.value = null
      }

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data)
          if (message.type === 'notification') {
            addNotification(message.data)
          } else if (message.type === 'callback_alert') {
            callbackAlert.value = message.data
          }
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error)
        }
      }

      ws.onclose = (event) => {
        wsConnected.value = false

        if (event.code === 4001) {
          wsError.value = 'Authentication failed — refreshing token'
          refreshAndReconnect()
          return
        }

        if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
          wsError.value = 'Max reconnection attempts reached'
          return
        }

        reconnectAttempts++
        const delay = Math.min(5000 * reconnectAttempts, 30000)
        reconnectTimer = setTimeout(() => {
          initWebSocket()
        }, delay)
      }

      ws.onerror = (error) => {
        wsError.value = 'WebSocket connection error'
        console.error('WebSocket error:', error)
      }
    } catch (error) {
      wsError.value = 'Failed to initialize WebSocket'
      console.error('WebSocket initialization error:', error)
    }
  }

  const refreshAndReconnect = async () => {
    const refreshToken = getRefreshToken()
    if (!refreshToken) {
      wsError.value = 'No refresh token available'
      return
    }

    try {
      const API_URL = import.meta.env.VITE_API_URL || ''
      const response = await fetch(`${API_URL}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${refreshToken}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error(`Refresh failed: ${response.status}`)
      }

      const data = await response.json()
      updateStoredTokens(data.access_token, data.refresh_token)

      // Reset attempts and reconnect with the fresh token.
      reconnectAttempts = 0
      initWebSocket()
    } catch (error) {
      wsError.value = 'Token refresh failed'
      console.error('Failed to refresh token for WebSocket:', error)
    }
  }

  const disconnectWebSocket = () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
    if (ws) {
      ws.close()
      ws = null
    }
    wsConnected.value = false
    reconnectAttempts = MAX_RECONNECT_ATTEMPTS
  }

  return {
    notifications,
    unreadCount,
    wsConnected,
    wsError,
    isLoading,
    unreadNotifications,
    readNotifications,
    loadNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    addNotification,
    initWebSocket,
    disconnectWebSocket,
    callbackAlert,
    refreshAndReconnect,
  }
})
